import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, Divider,
  IconButton, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useCotizacionStore } from '../store/cotizacionStore';
import type { CotizacionCreateRequest, CotizacionDetalleRequest } from '../types/cotizacion.types';
import { initialCotizacionCreate } from '../types/cotizacion.types';

const emptyDetalle: CotizacionDetalleRequest = { idArticulo: 0, descripcion: '', cantidad: 1, precioUnitario: 0 };

export const CotizacionFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'nueva';
  const { selectedCotizacion, loading, fetchCotizacionById, saveCotizacion, convertirAVenta } = useCotizacionStore();

  const [formData, setFormData] = useState<CotizacionCreateRequest>({ ...initialCotizacionCreate });

  useEffect(() => { if (isEdit && id) fetchCotizacionById(parseInt(id)); }, [id]);

  useEffect(() => {
    if (isEdit && selectedCotizacion) {
      setFormData({
        idPersona: selectedCotizacion.idPersona || 0,
        idVendedor: selectedCotizacion.idVendedor,
        idMoneda: selectedCotizacion.idMoneda,
        idListaPrecio: selectedCotizacion.idListaPrecio,
        numero: selectedCotizacion.numero,
        fechaValidez: selectedCotizacion.fechaValidez,
        observaciones: selectedCotizacion.observaciones,
        detalles: selectedCotizacion.detalles?.map((d) => ({
          idArticulo: d.idArticulo || 0, descripcion: d.descripcion || '',
          cantidad: d.cantidad, precioUnitario: d.precioUnitario, descuento: d.descuento,
        })) || [],
      });
    }
  }, [selectedCotizacion]);

  const handleChange = (field: keyof CotizacionCreateRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDetalleChange = (index: number, field: keyof CotizacionDetalleRequest, value: any) => {
    setFormData((prev) => {
      const detalles = [...prev.detalles];
      detalles[index] = { ...detalles[index], [field]: value };
      return { ...prev, detalles };
    });
  };

  const addDetalle = () => setFormData((prev) => ({ ...prev, detalles: [...prev.detalles, { ...emptyDetalle }] }));
  const removeDetalle = (index: number) => setFormData((prev) => ({ ...prev, detalles: prev.detalles.filter((_, i) => i !== index) }));

  const totales = useMemo(() => {
    let subTotal = 0;
    formData.detalles.forEach((d) => { subTotal += d.cantidad * d.precioUnitario - (d.descuento ?? 0); });
    const igv = subTotal * 0.18;
    return { subTotal, igv, total: subTotal + igv };
  }, [formData.detalles]);

  const handleSubmit = async () => {
    try { await saveCotizacion(formData); navigate('/ventas/cotizaciones'); } catch { /* handled */ }
  };

  const handleConvertir = async () => {
    if (isEdit && id) {
      try { await convertirAVenta(parseInt(id)); navigate('/ventas/explorador'); } catch { /* handled */ }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/ventas/cotizaciones')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight="bold">{isEdit ? 'Editar Cotización' : 'Nueva Cotización'}</Typography>
        {isEdit && (
          <Button variant="outlined" color="success" startIcon={<ShoppingCartIcon />} onClick={handleConvertir} sx={{ ml: 'auto' }}>
            Convertir a Venta
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos Generales</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><TextField label="ID Cliente" size="small" fullWidth type="number" value={formData.idPersona || ''} onChange={(e) => handleChange('idPersona', parseInt(e.target.value) || 0)} /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><TextField label="ID Vendedor" size="small" fullWidth type="number" value={formData.idVendedor || ''} onChange={(e) => handleChange('idVendedor', parseInt(e.target.value) || null)} /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><TextField label="Número" size="small" fullWidth value={formData.numero || ''} onChange={(e) => handleChange('numero', e.target.value)} /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><TextField label="Fecha Validez" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }} value={formData.fechaValidez || ''} onChange={(e) => handleChange('fechaValidez', e.target.value)} /></Grid>
          <Grid size={{ xs: 12 }}><TextField label="Observaciones" size="small" fullWidth multiline rows={2} value={formData.observaciones || ''} onChange={(e) => handleChange('observaciones', e.target.value)} /></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Detalle</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addDetalle} size="small">Agregar Línea</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={50}>#</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell width={100}>Cantidad</TableCell>
                <TableCell width={120}>P. Unitario</TableCell>
                <TableCell width={100}>Descuento</TableCell>
                <TableCell width={120}>SubTotal</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.detalles.map((det, idx) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell><TextField size="small" fullWidth value={det.descripcion} onChange={(e) => handleDetalleChange(idx, 'descripcion', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.cantidad} onChange={(e) => handleDetalleChange(idx, 'cantidad', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.precioUnitario} onChange={(e) => handleDetalleChange(idx, 'precioUnitario', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.descuento ?? 0} onChange={(e) => handleDetalleChange(idx, 'descuento', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell align="right">S/ {(det.cantidad * det.precioUnitario - (det.descuento ?? 0)).toFixed(2)}</TableCell>
                  <TableCell><IconButton size="small" color="error" onClick={() => removeDetalle(idx)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              {formData.detalles.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin líneas de detalle</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ minWidth: 250 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="body2">Sub Total:</Typography><Typography variant="body2">S/ {totales.subTotal.toFixed(2)}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="body2">IGV (18%):</Typography><Typography variant="body2">S/ {totales.igv.toFixed(2)}</Typography></Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="h6">Total:</Typography><Typography variant="h6">S/ {totales.total.toFixed(2)}</Typography></Box>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={() => navigate('/ventas/cotizaciones')}>Cancelar</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={loading}>{loading ? 'Guardando...' : 'Guardar'}</Button>
      </Box>
    </Box>
  );
};
